import React, { Component } from 'react';

import cn from 'classnames';
import { noop } from 'lodash';
import {
  node,
  oneOf,
  bool,
  oneOfType,
  shape,
  func,
  number,
  string,
} from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { faFlag } from '@fortawesome/pro-regular-svg-icons/faFlag';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons/faTrashCan';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import { faClock } from '@fortawesome/pro-solid-svg-icons/faClock';

import IconButton from '@mui/material/IconButton';
import MuiSnackbar from '@mui/material/Snackbar';

import * as snackbarActions from 'actions/snackbar';
import Flexbox from 'components/Common/Flexbox';
import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';
import Text from 'components/Common/Text';
import TextLink from 'components/Common/TextLink';
import withMediaInfo from 'HOC/withMediaInfo';

import s from './Snackbar.css';

@withMediaInfo()
@connect(
  ({ snackbar }) => ({
    snackbarMessage: snackbar.snackbarMessage,
    snackbarVariant: snackbar.variant,
    persistent: snackbar.persistent,
    autoHideDuration: snackbar.autoHideDuration,
    icon: snackbar.icon,
    hideCloseIcon: snackbar.hideCloseIcon,
    additionalText: snackbar.additionalText,
  }),
  dispatch => bindActionCreators({ ...snackbarActions }, dispatch),
)
export default class Snackbar extends Component {
  render() {
    const {
      snackbarMessage,
      setSnackbarMessage,
      closeSnackbarMessage,
      autoHideDuration,
      snackbarVariant,
      persistent,
      icon,
      mediaInfo: { isMobile },
      hideCloseIcon,
      additionalText,
      linkProps,
      ...restProps
    } = this.props;

    const snackbarIcon = {
      default: <FontAwesomeIcon fixedWidth icon={faCircleCheck} />,
      error: <FontAwesomeIcon fixedWidth icon={faCircleExclamation} />,
      info: null,
      infoGray: null,
      deleted: <FontAwesomeIcon fixedWidth icon={faTrashCan} />,
      flag: <FontAwesomeIcon fixedWidth icon={faFlag} />,
      clock: <FontAwesomeIcon fixedWidth icon={faClock} color="#fff" />,
    };

    const snackbarStyle = {
      default: s.default,
      error: s.error,
      info: s.info,
      infoGray: s.infoGray,
      deleted: s.deleted,
      white: s.white,
    };

    const onClose = () => closeSnackbarMessage(snackbarVariant);
    const textColor = snackbarVariant === 'white' ? 'black' : 'white';
    const linkColor = snackbarVariant === 'white' ? 'blueDark' : 'white';

    return (
      <MuiSnackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        classes={{
          root: cn(s.snackbar, { [s.mobileSnackbar]: isMobile }),
        }}
        disableWindowBlurListener
        onClose={onClose}
        autoHideDuration={persistent ? null : autoHideDuration}
        open={Boolean(snackbarMessage)}
        onClick={onClose}
        ContentProps={{
          'aria-describedby': 'message-id',
          classes: {
            root: cn(s.snackbarContent, snackbarStyle[snackbarVariant]),
          },
        }}
        message={
          <Flexbox
            id="message-id"
            alignItems={additionalText ? 'flex-start' : 'center'}
            gap={1}
            className={s.messageContainer}
          >
            {(icon && (typeof icon === 'string' ? snackbarIcon[icon] : icon)) ||
              snackbarIcon[snackbarVariant]}
            <Flexbox flexDirection="column" gap={0.5}>
              <Text variant="bodySm" className={s.message} color={textColor}>
                <strong>{snackbarMessage}</strong>
              </Text>
              {additionalText && (
                <Text
                  variant="meta"
                  className={s.additionalText}
                  color={textColor}
                >
                  {additionalText}
                </Text>
              )}
              {linkProps && (
                <TextLink
                  href={linkProps.href}
                  color={linkColor}
                  forceUnderline
                >
                  {linkProps.label}
                </TextLink>
              )}
            </Flexbox>
          </Flexbox>
        }
        action={[
          ...((isMobile && !persistent) || !!hideCloseIcon
            ? []
            : [
                <IconButton
                  key="close"
                  aria-label="Close"
                  color="inherit"
                  onClick={onClose}
                >
                  <FontAwesomeIcon color="white" icon={faXmark} />
                </IconButton>,
              ]),
        ]}
        {...restProps}
      />
    );
  }
}

Snackbar.propTypes = {
  /** The message to display. */
  snackbarMessage: node,
  /** The snackbar variant */
  snackbarVariant: oneOf(['default', 'error', 'info', 'infoGray', 'deleted']),
  /** If true, the snackbar will not close until the user clicks somewhere */
  persistent: bool,
  /** Displays an icon in the snackbar */
  icon: oneOfType([
    node,
    oneOf(['default', 'error', 'info', 'infoGray', 'deleted', 'flag', 'clock']),
  ]),
  /** Used to handle mobile responsiveness */
  mediaInfo: shape({
    isMobile: bool,
    isTablet: bool,
    isDesktop: bool,
  }).isRequired,
  /** If true, hides the close icon */
  hideCloseIcon: bool,
  /** Callback function used to set the snackbar message */
  setSnackbarMessage: func,
  /** Callback fired when the component requests to be closed */
  closeSnackbarMessage: func,
  /** The number of milliseconds to wait before automatically calling the onClose function */
  autoHideDuration: number,
  additionalText: string,
  linkProps: shape({
    href: string,
    label: string,
  }),
};

Snackbar.defaultProps = {
  snackbarMessage: '',
  snackbarVariant: 'default',
  persistent: false,
  icon: undefined,
  hideCloseIcon: false,
  setSnackbarMessage: noop,
  closeSnackbarMessage: noop,
  autoHideDuration: 4000,
  additionalText: '',
  linkProps: undefined,
};
